import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { AlertCircle } from '@untitledui/icons/AlertCircle';
import { CheckCircle } from '@untitledui/icons/CheckCircle';
import { useEffect, useState } from 'react';

import { Button } from '@/components/base/buttons/button';
import { useResendVerificationEmail } from '@/hooks/api/use-resend-verification-email';
import { useAnalytics } from '@/lib/analytics/use-analytics';
import { DecorativeCircles } from '@/routes/participants/-components/decorative-circles';
import { SignupCarousel } from '@/routes/participants/-components/signup-carousel';
import { getErrorMessage } from '@/utils/error';

import { AuthLayout } from '../-components/auth-layout';
import { requireParticipantSession } from '../-utils/participant-route-guards';

export const Route = createFileRoute('/participants/verify-email/')({
  beforeLoad: ({ context, location }) => {
    const { user, env } = context;

    requireParticipantSession(user, env, location);

    if (user.isEmailVerified) {
      throw redirect({ to: '/participants' });
    }
    return { user };
  },
  component: VerifyEmailPage,
});

function VerifyEmailPage() {
  const { user } = Route.useRouteContext();
  const analytics = useAnalytics();
  const email = user.email;

  // email_verification_sent: User sees the 'Check your email' screen
  useEffect(() => {
    analytics?.screen({ name: 'emailVerification' });
  }, [analytics]);

  return (
    <AuthLayout rightPanel={<SignupCarousel />}>
      <div className="flex flex-col items-center text-center">
        <DecorativeCircles>
          <span className="text-display-sm leading-none">{'\u{1F4EB}'}</span>
        </DecorativeCircles>

        {/* Heading */}
        <h1 className="z-10 mt-2 font-heading text-display-sm font-semibold text-primary">
          <Trans>Check your email</Trans>
        </h1>

        {/* Body */}
        <p className="z-10 mt-3 text-md text-primary">
          <Trans>
            We&apos;ve sent a verification link to your email{' '}
            <span className="font-bold">{email}</span>. Just click the link in
            that message to activate your account and get started.
          </Trans>
        </p>

        {/* Spam notice + resend */}
        <p className="mt-8 mb-8 text-sm text-tertiary">
          <Trans>
            Double-check your spam or junk folder if you don&apos;t see the
            email within a few minutes.
          </Trans>{' '}
        </p>
        <ResendLink />
        <Link
          to="/sign-out"
          search={SIGN_OUT_TO_SIGNUP_SEARCH}
          className="mt-6 text-sm text-tertiary"
        >
          <Trans>Sign up with a different email</Trans>
        </Link>
      </div>
    </AuthLayout>
  );
}

const COOLDOWN_SECONDS = 60;
const SIGN_OUT_TO_SIGNUP_SEARCH = { to: '/participants/signup' };

function ResendLink() {
  const [cooldown, setCooldown] = useState(0);
  const resendMutation = useResendVerificationEmail();

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleResend = () => {
    if (resendMutation.isPending || cooldown > 0) return;
    resendMutation.mutate(undefined, {
      onSuccess: () => {
        setCooldown(COOLDOWN_SECONDS);
      },
    });
  };

  const resendError = resendMutation.isError
    ? (getErrorMessage(resendMutation.error) ??
      t`Failed to resend email. Please try again later.`)
    : undefined;

  return (
    <div className="flex flex-col items-center gap-2">
      {cooldown > 0 && (
        <span className="inline-flex items-center gap-1 font-semibold text-success-primary">
          <CheckCircle className="size-4" />
          <Trans>Email sent!</Trans>
        </span>
      )}
      <Button
        color="link-color"
        size="sm"
        isLoading={resendMutation.isPending}
        isDisabled={cooldown > 0}
        onClick={handleResend}
      >
        {cooldown > 0 ? (
          <Trans>Resend in {cooldown}s</Trans>
        ) : (
          <Trans>Resend email</Trans>
        )}
      </Button>
      {resendMutation.isError && (
        <span className="inline-flex items-center gap-1 text-sm text-error-primary">
          <AlertCircle className="inline size-4" />
          {resendError}
        </span>
      )}
    </div>
  );
}
